<script lang="ts">
	import { dev } from '$app/env';
	import { page } from '$app/state';
	import Modal from '#lib/modern/Modal.svelte';
	import { activityStore } from '#lib/stores/activity.js';
	import { agendaSettingStore } from '#lib/stores/agenda.js';
	import { apiLogStore } from '#lib/stores/api.js';
	import { areaStore } from '#lib/stores/area.js';
	import { blockingPeriods } from '#lib/stores/blocking-period.js';
	import { bookingStore } from '#lib/stores/booking.js';
	import { businessStore } from '#lib/stores/business.js';
	import { customerStore } from '#lib/stores/customer.js';
	import {
		clearDiagnostics,
		closeDiagnostics,
		diagnosticsStore,
		errorCount,
		exportDiagnosticsReport,
		isDiagnosticsModalOpen,
		navigationHistoryStore
	} from '#lib/stores/diagnostics.js';
	import CopyButton from '#lib/modern/CopyButton.svelte';
	import ModernBadge from '#lib/modern/ModernBadge.svelte';
	import SegmentedControl, { type Option } from '#lib/modern/SegmentedControl.svelte';
	import { featureFlipStore } from '#lib/stores/feature-flip.js';
	import { t } from '#lib/stores/i18n.js';
	import { notificationSettingStore } from '#lib/stores/notification-setting.js';
	import { offerStore } from '#lib/stores/offer.js';
	import { openingHourExceptionStore } from '#lib/stores/opening-hour-exception.js';
	import { sessionStore } from '#lib/stores/session.js';
	import { sessionInstanceStore } from '#lib/stores/session-instance.js';
	import { socketStore } from '#lib/stores/socket.js';
	import { adminStore } from '#lib/stores/admin.js';
	import { isTimeSimulated, now, resetTimeTravel, businessTimezone } from '#lib/stores/time.js';
	import { userStore } from '#lib/stores/user.js';
	import { Activity, Clock, Database, Globe, Trash2, Wifi } from '@lucide/svelte';
	import DiagnosticsApiTab from './diagnostics/DiagnosticsApiTab.svelte';
	import DiagnosticsIncidentsTab from './diagnostics/DiagnosticsIncidentsTab.svelte';
	import DiagnosticsSocketTab from './diagnostics/DiagnosticsSocketTab.svelte';
	import DiagnosticsStateTab from './diagnostics/DiagnosticsStateTab.svelte';
	import DiagnosticsTimeTravelTab from './diagnostics/DiagnosticsTimeTravelTab.svelte';

	let isOpen = $derived($isDiagnosticsModalOpen);

	type TabType = 'incidents' | 'api' | 'socket' | 'state' | 'timetravel';
	let activeTab = $state<TabType>('incidents');

	let tabOptions = $derived<Option<TabType>[]>([
		{ value: 'incidents', label: $t('diagnostics.tab.logs') },
		{ value: 'api', label: $t('diagnostics.tab.api') },
		{ value: 'socket', label: $t('diagnostics.tab.socket') },
		{ value: 'state', label: $t('diagnostics.tab.state') },
		...($adminStore || dev
			? [
					{
						value: 'timetravel' as TabType,
						label: $t('diagnostics.tab.timetravel')
					}
				]
			: [])
	]);

	$effect(() => {
		if (!$adminStore && !dev) {
			if (activeTab === 'timetravel') {
				activeTab = 'incidents';
			}
			if ($isTimeSimulated) {
				resetTimeTravel();
			}
		}
	});

	function getCompleteDiagnosticsText(): string {
		try {
			const reportMarkdown = exportDiagnosticsReport($apiLogStore, $navigationHistoryStore);
			const socketData = Object.fromEntries(
				Object.entries($socketStore).filter(([k]) => k !== 'socket')
			);
			const data = {
				generatedAt: $now.toISO(),
				timezone: $businessTimezone,
				isTimeSimulated: $isTimeSimulated,
				diagnostics: $diagnosticsStore,
				navigationHistory: $navigationHistoryStore,
				socket: socketData,
				apiLogs: $apiLogStore,
				user: $userStore,
				business: $businessStore,
				activity: $activityStore,
				booking: $bookingStore,
				session: $sessionStore,
				area: $areaStore,
				offer: $offerStore,
				customer: $customerStore,
				featureFlip: $featureFlipStore,
				blockingPeriods: $blockingPeriods,
				sessionInstance: $sessionInstanceStore,
				agendaSettings: $agendaSettingStore,
				notificationSettings: $notificationSettingStore,
				openingHourExceptions: $openingHourExceptionStore,
				page: { url: page.url.href, pathname: page.url.pathname, params: page.params }
			};

			return `${reportMarkdown}\n\n## Full System State (JSON)\n\`\`\`json\n${JSON.stringify(data, null, 2)}\n\`\`\``;
		} catch {
			return exportDiagnosticsReport($apiLogStore, $navigationHistoryStore);
		}
	}
</script>

{#if isOpen}
	<Modal canSkip={true} maxWidthClass="max-w-4xl" onclose={closeDiagnostics}>
		<div
			class="relative w-full rounded-3xl shadow-2xl border border-border/80 dark:border-white/10 bg-surface dark:bg-[#111520] text-surface-on dark:text-slate-100 overflow-hidden backdrop-blur-2xl transition-all flex flex-col"
		>
			<!-- Unified Header (consistent display across admin and non-admin) -->
			<div
				class="p-6 pb-4 flex items-center justify-between border-b border-border/60 dark:border-white/10 flex-wrap gap-3"
			>
				<div class="flex items-center gap-3.5">
					<div
						class="p-2.5 rounded-2xl border shadow-sm shrink-0 bg-primary/10 text-primary border-primary/20"
					>
						<Activity class="w-6 h-6" />
					</div>
					<div>
						<div class="flex items-center gap-2">
							<h2
								class="text-lg font-bold tracking-tight text-surface-on dark:text-white"
							>
								{$t('diagnostics.title')}
							</h2>
							<ModernBadge variant="neutral" className="text-2xs font-semibold">
								{$t('diagnostics.shortcut')}
							</ModernBadge>
						</div>
						<p class="text-xs text-surface-on/60 dark:text-slate-400 mt-0.5">
							{$t('diagnostics.subtitle')}
						</p>
					</div>
				</div>

				<div class="flex items-center gap-2">
					{#if $adminStore || dev}
						<CopyButton
							text={exportDiagnosticsReport($apiLogStore, $navigationHistoryStore)}
							title={$t('diagnostics.copy_report')}
							toastMessage={$t('diagnostics.copied')}
							class="px-3 py-1.5 text-xs font-semibold rounded-xl bg-admin/10 hover:bg-admin/20 text-admin transition-all shadow-xs"
						>
							{$t('diagnostics.copy_report')}
						</CopyButton>
					{/if}
					<CopyButton
						getText={getCompleteDiagnosticsText}
						title={$t('diagnostics.copy_complete_data')}
						toastMessage={$t('diagnostics.complete_data_copied')}
						class="px-3 py-1.5 text-xs font-semibold rounded-xl bg-primary text-primary-on hover:opacity-90 active:scale-95 transition-all shadow-xs"
					>
						{$t('diagnostics.copy_complete_data')}
					</CopyButton>
					<button
						type="button"
						disabled={$diagnosticsStore.length === 0 && $apiLogStore.length === 0}
						class="px-3 py-1.5 text-xs font-medium rounded-xl border border-border/80 dark:border-white/10 hover:bg-surface-on/5 dark:hover:bg-white/5 text-surface-on/70 dark:text-slate-300 hover:text-error dark:hover:text-red-400 transition-all flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
						onclick={() => {
							clearDiagnostics();
							apiLogStore.set([]);
						}}
					>
						<Trash2 class="w-3.5 h-3.5" />
						{$t('diagnostics.clear')}
					</button>
				</div>
			</div>

			<!-- Tabs Navigation Bar -->
			<div
				class="px-6 pt-3 pb-2 flex items-center justify-between gap-3 border-b border-border/60 dark:border-white/10 flex-wrap"
			>
				<SegmentedControl options={tabOptions} bind:value={activeTab}>
					{#snippet children(option)}
						<div class="flex items-center gap-2">
							{#if option.value === 'incidents'}
								<Activity class="w-4 h-4" />
							{:else if option.value === 'api'}
								<Globe class="w-4 h-4" />
							{:else if option.value === 'socket'}
								<Wifi class="w-4 h-4" />
							{:else if option.value === 'state'}
								<Database class="w-4 h-4" />
							{:else if option.value === 'timetravel'}
								<Clock class="w-4 h-4 text-admin" />
							{/if}
							<span>{option.label}</span>
							{#if option.value === 'incidents'}
								{#if $errorCount > 0}
									<ModernBadge
										variant="error"
										className="text-2xs px-1.5 py-0.2 font-bold"
									>
										{$errorCount}
									</ModernBadge>
								{:else if $diagnosticsStore.length > 0}
									<ModernBadge
										variant="neutral"
										className="text-2xs px-1.5 py-0.2 font-semibold"
									>
										{$diagnosticsStore.length}
									</ModernBadge>
								{/if}
							{:else if option.value === 'api' && $apiLogStore.length > 0}
								<ModernBadge
									variant="neutral"
									className="text-2xs px-1.5 py-0.2 font-semibold"
								>
									{$apiLogStore.length}
								</ModernBadge>
							{:else if option.value === 'socket'}
								<span
									class="w-2 h-2 rounded-full {$socketStore.connected
										? 'bg-emerald-500'
										: 'bg-red-500'}"
								></span>
								{#if $socketStore.messages.length > 0}
									<ModernBadge
										variant="neutral"
										className="text-2xs px-1.5 py-0.2 font-semibold"
									>
										{$socketStore.messages.length}
									</ModernBadge>
								{/if}
							{:else if option.value === 'timetravel'}
								<span
									class="px-1.5 py-0.2 rounded-full text-2xs font-bold bg-admin text-admin-on"
								>
									{$adminStore ? 'Admin' : 'Dev'}
								</span>
								{#if $isTimeSimulated}
									<span class="w-2 h-2 rounded-full bg-amber-400 animate-pulse"
									></span>
								{/if}
							{/if}
						</div>
					{/snippet}
				</SegmentedControl>
			</div>

			<!-- Tab Content Body -->
			<div class="p-6 transition-colors {activeTab === 'timetravel' ? 'bg-admin/5' : ''}">
				{#if activeTab === 'incidents'}
					<DiagnosticsIncidentsTab />
				{:else if activeTab === 'api'}
					<DiagnosticsApiTab />
				{:else if activeTab === 'socket'}
					<DiagnosticsSocketTab />
				{:else if activeTab === 'state'}
					<DiagnosticsStateTab />
				{:else if activeTab === 'timetravel' && ($adminStore || dev)}
					<DiagnosticsTimeTravelTab />
				{/if}
			</div>
		</div>
	</Modal>
{/if}
